import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { ClipboardDocumentListIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import BreadCrumb from '../BreadCrumb';
import { formatCurrency } from '../Help/helper';

const pages = [
  { name: 'Procurement', to: '/procurement', component: Link, current: false },
  { name: 'Procurement Status', to: '/procurement/manage', component: Link, current: true },
];

const ManageProcurements = () => {
  const [procurements, setProcurements] = useState([]);
  const [filteredProcurements, setFilteredProcurements] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchProcurements = async () => {
      const querySnapshot = await getDocs(collection(db, 'procurements'));
      const items = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        const statusUpdateDate = data.statusUpdateDate?.toDate
          ? data.statusUpdateDate.toDate()
          : data.statusUpdateDate;
        return { id: doc.id, ...data, statusUpdateDate };
      });
      setProcurements(items);
    };

    fetchProcurements();
  }, []);

  useEffect(() => {
    // Apply search and filters
    let tempProcurements = [...procurements];

    // Search
    if (searchTerm) {
      tempProcurements = tempProcurements.filter((item) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (item.procurementNumber && item.procurementNumber.toLowerCase().includes(searchLower)) ||
          (item.supplier && item.supplier.toLowerCase().includes(searchLower))
        );
      });
    }

    // Filter by status
    if (filterStatus) {
      tempProcurements = tempProcurements.filter((item) => item.status === filterStatus);
    }

    setFilteredProcurements(tempProcurements);
    setCurrentPage(1); // Reset to first page when filters change
  }, [procurements, searchTerm, filterStatus]);

  const handleStatusChange = async (id, newStatus) => {
    const docRef = doc(db, 'procurements', id);
    await updateDoc(docRef, {
      status: newStatus,
      statusUpdateDate: new Date(),
    });

    setProcurements((prevProcurements) =>
      prevProcurements.map((item) =>
        item.id === id ? { ...item, status: newStatus, statusUpdateDate: new Date() } : item
      )
    );
  };

  // Pagination logic
  const indexOfLastProcurement = currentPage * itemsPerPage;
  const indexOfFirstProcurement = indexOfLastProcurement - itemsPerPage;
  const currentProcurements = filteredProcurements.slice(
    indexOfFirstProcurement,
    indexOfLastProcurement
  );
  const totalPages = Math.ceil(filteredProcurements.length / itemsPerPage);

  const getStatusBadge = (status) => {
    const badges = {
      Ordered: 'bg-blue-100 text-blue-800',
      Collected: 'bg-green-100 text-green-800',
      'Defective-Returned': 'bg-red-100 text-red-800',
      'Warranty-Replaced': 'bg-purple-100 text-purple-800',
      'Refund-Requested': 'bg-amber-100 text-amber-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Page Header */}
        <div className="mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl shadow-lg">
              <ClipboardDocumentListIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-gray-900">Manage Procurements</h1>
              <p className="text-sm text-gray-600 mt-1">Update delivery status and track orders</p>
            </div>
          </div>
        </div>

        {/* Search and Filter Controls - Compact */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by Procurement No or Supplier"
                  className="block w-full pl-10 pr-3 py-2 text-sm border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div>
              <select
                className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="Ordered">Ordered</option>
                <option value="Collected">Collected</option>
                <option value="Defective-Returned">Defective-Returned</option>
                <option value="Warranty-Replaced">Warranty-Replaced</option>
                <option value="Refund-Requested">Refund-Requested</option>
              </select>
            </div>
          </div>
        </div>

        {/* Procurements Table - Compact */}
        <div className="bg-white shadow-lg rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-indigo-600 to-purple-600">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Proc No
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Supplier
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-bold text-white uppercase tracking-wider">
                    Change Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentProcurements.map((item) => (
                  <tr key={item.id} className="hover:bg-indigo-50 transition-colors">
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs font-semibold text-gray-900">
                      {item?.procurementNumber}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-600">
                      {item.supplier}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs">
                      <span
                        className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${getStatusBadge(
                          item.status
                        )}`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs font-bold text-gray-900">
                      {formatCurrency(item.grandTotal)}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap text-xs text-gray-600">
                      {item.statusUpdateDate
                        ? moment(item.statusUpdateDate).format('DD-MMM-YYYY HH:mm')
                        : 'N/A'}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <select
                        className="border border-gray-300 rounded-xl px-2 py-1 text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        value={item.status}
                        onChange={(e) => handleStatusChange(item.id, e.target.value)}
                      >
                        <option value="Ordered">Ordered</option>
                        <option value="Collected">Collected</option>
                        <option value="Defective-Returned">Defective-Returned</option>
                        <option value="Warranty-Replaced">Warranty-Replaced</option>
                        <option value="Refund-Requested">Refund-Requested</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Controls - Compact */}
        <div className="flex justify-between mt-4 items-center bg-white rounded-xl shadow-lg p-3">
          <button
            onClick={() => setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-xl text-sm font-semibold ${
              currentPage === 1
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 shadow-md'
            }`}
          >
            Previous
          </button>
          <span className="text-sm font-semibold text-gray-700">
            Page {currentPage} of {totalPages || 1}
          </span>
          <button
            onClick={() => setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages || 1))}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`px-4 py-2 rounded-xl text-sm font-semibold ${
              currentPage === totalPages || totalPages === 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 shadow-md'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageProcurements;
